import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  return (
    <nav className="bg-gradient-to-r from-nba-blue to-nba-red text-white shadow-lg dark:shadow-gray-900 relative z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
            <img 
              src="/images/2k-buli-logo.jpeg" 
              alt="2K Bundesliga Logo"
              className="h-10 md:h-12 w-auto object-contain"
              onError={(e) => { 
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <span className="text-xl md:text-2xl font-bold ml-2 hidden">
              <span className="hidden sm:inline">2K BUNDESLIGA</span>
              <span className="sm:hidden">2K BL</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-6 items-center">
            <Link to="/" className="hover:text-gray-200 transition font-semibold">{t('nav.home')}</Link>
            
            {/* League Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setOpenDropdown('league')}
              onMouseLeave={closeDropdown}
            >
              <button
                className="hover:text-gray-200 transition flex items-center gap-1 font-semibold"
              >
                {t('nav.league', 'League')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdown === 'league' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg shadow-xl py-2 w-48">
                    <Link to="/standings" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      📊 {t('nav.standings')}
                    </Link>
                    <Link to="/game-periods" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🗓️ {t('nav.gamePeriods')}
                    </Link>
                    <Link to="/stats" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      📈 {t('nav.stats')}
                    </Link>
                    <Link to="/playoffs" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🏆 {t('nav.playoffs')}
                    </Link>
                    <Link to="/finals" className="block px-4 py-2 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 font-semibold transition" onClick={closeDropdown}>
                      🏆 {t('nav.finals', 'Finals')}
                    </Link>
                    <Link to="/champions" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🏆 {t('nav.champions')}
                    </Link>
                    <Link to="/former-champions" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      👑 {t('nav.formerChampions')}
                    </Link>
                    <Link to="/awards" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🏅 {t('nav.awards', 'Awards')}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Teams Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setOpenDropdown('teams')}
              onMouseLeave={closeDropdown}
            >
              <button
                className="hover:text-gray-200 transition flex items-center gap-1 font-semibold"
              >
                {t('nav.teams')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdown === 'teams' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg shadow-xl py-2 w-48">
                    <Link to="/teams" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🏀 {t('nav.teams')}
                    </Link>
                    <Link to="/players" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      👥 {t('nav.players')}
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* News Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setOpenDropdown('news')}
              onMouseLeave={closeDropdown}
            >
              <button
                className="hover:text-gray-200 transition flex items-center gap-1 font-semibold"
              >
                {t('nav.news', 'News')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdown === 'news' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg shadow-xl py-2 w-48">
                    <Link to="/news/owners" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      👥 {t('nav.ownerNews', 'Owner News')}
                    </Link>
                    <Link to="/news/trades" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      🔄 {t('nav.tradeNews', 'Trade News')}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Analytics Link */}
            <Link to="/analytics" className="hover:text-gray-200 transition font-semibold">📊 {t('nav.analytics', 'Analytics')}</Link>

            {/* Info Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setOpenDropdown('info')}
              onMouseLeave={closeDropdown}
            >
              <button
                className="hover:text-gray-200 transition flex items-center gap-1 font-semibold"
              >
                {t('nav.info', 'Info')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openDropdown === 'info' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg shadow-xl py-2 w-48">
                    <Link to="/rules" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      📜 {t('nav.rules')}
                    </Link>
                    <Link to="/about" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      ℹ️ {t('nav.about')}
                    </Link>
                    <Link to="/team-owners" className="block px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition" onClick={closeDropdown}>
                      👥 {t('nav.teamOwners')}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 transition"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                <Link to="/settings" className="hover:text-gray-200 transition font-semibold text-sm">
                  ⚙️ {t('nav.settings')}
                </Link>
                <Link to="/admin" className="bg-white dark:bg-gray-800 text-nba-blue px-4 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 transition font-semibold text-sm">
                  {t('nav.admin')}
                </Link>
                <span className="text-sm hidden xl:inline">{user.username}</span>
                <button
                  onClick={logout}
                  className="bg-red-600 px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold text-sm"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/apply" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold text-sm">
                  {t('nav.apply')}
                </Link>
                <Link to="/login" className="bg-white dark:bg-gray-800 text-nba-blue px-4 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 transition font-semibold text-sm">
                  {t('nav.login')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 transition"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-white border-opacity-20 mt-2 pt-4">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                className="hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold"
                onClick={closeMobileMenu}
              >
                {t('nav.home')}
              </Link>

              {/* League Section in Mobile */}
              <div>
                <button
                  onClick={() => toggleDropdown('mobile-league')}
                  className="w-full text-left hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold flex items-center justify-between"
                >
                  {t('nav.league', 'League')}
                  <svg 
                    className={`w-4 h-4 transition-transform ${openDropdown === 'mobile-league' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openDropdown === 'mobile-league' && (
                  <div className="pl-6 pr-4 py-2 space-y-1">
                    <Link
                      to="/standings"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      📊 {t('nav.standings')}
                    </Link>
                    <Link
                      to="/game-periods"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      🗓️ {t('nav.gamePeriods')}
                    </Link>
                    <Link
                      to="/stats"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      📈 {t('nav.stats')}
                    </Link>
                    <Link
                      to="/playoffs"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      🏆 {t('nav.playoffs')}
                    </Link>
                    <Link
                      to="/champions"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      👑 {t('nav.champions')}
                    </Link>
                    <Link
                      to="/awards"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      🏅 {t('nav.awards', 'Awards')}
                    </Link>
                  </div>
                )}
              </div>

              {/* Teams Section in Mobile */}
              <div>
                <button
                  onClick={() => toggleDropdown('mobile-teams')}
                  className="w-full text-left hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold flex items-center justify-between"
                >
                  {t('nav.teams')}
                  <svg 
                    className={`w-4 h-4 transition-transform ${openDropdown === 'mobile-teams' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openDropdown === 'mobile-teams' && (
                  <div className="pl-6 pr-4 py-2 space-y-1">
                    <Link
                      to="/teams"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      🏀 {t('nav.teams')}
                    </Link>
                    <Link
                      to="/players"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      👥 {t('nav.players')}
                    </Link>
                  </div>
                )}
              </div>
              
              {/* News Section in Mobile */}
              <div>
                <button
                  onClick={() => toggleDropdown('mobile-news')}
                  className="w-full text-left hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold flex items-center justify-between"
                >
                  {t('nav.news', 'News')}
                  <svg 
                    className={`w-4 h-4 transition-transform ${openDropdown === 'mobile-news' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openDropdown === 'mobile-news' && (
                  <div className="pl-6 pr-4 py-2 space-y-1">
                    <Link
                      to="/news/owners"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      👥 {t('nav.ownerNews', 'Owner News')}
                    </Link>
                    <Link
                      to="/news/trades"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      🔄 {t('nav.tradeNews', 'Trade News')}
                    </Link>
                  </div>
                )}
              </div>

              {/* Analytics in Mobile */}
              <Link
                to="/analytics"
                className="hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold"
                onClick={closeMobileMenu}
              >
                📊 {t('nav.analytics', 'Analytics')}
              </Link>

              {/* Info Section in Mobile */}
              <div>
                <button
                  onClick={() => toggleDropdown('mobile-info')}
                  className="w-full text-left hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition font-semibold flex items-center justify-between"
                >
                  {t('nav.info', 'Info')}
                  <svg 
                    className={`w-4 h-4 transition-transform ${openDropdown === 'mobile-info' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openDropdown === 'mobile-info' && (
                  <div className="pl-6 pr-4 py-2 space-y-1">
                    <Link
                      to="/rules"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      📜 {t('nav.rules')}
                    </Link>
                    <Link
                      to="/about"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      ℹ️ {t('nav.about')}
                    </Link>
                    <Link
                      to="/team-owners"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition"
                      onClick={closeMobileMenu}
                    >
                      👥 {t('nav.teamOwners')}
                    </Link>
                  </div>
                )}
              </div>
              
              <div className="border-t border-white border-opacity-20 pt-3 mt-3">
                <div className="flex justify-center items-center gap-3 mb-4">
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-lg hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 transition"
                    aria-label="Toggle dark mode"
                  >
                    {theme === 'dark' ? '☀️' : '🌙'}
                  </button>
                  <LanguageSwitcher />
                </div>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/settings"
                      className="block hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 px-4 py-2 rounded transition mb-2"
                      onClick={closeMobileMenu}
                    >
                      ⚙️ {t('nav.settings')}
                    </Link>
                    <Link
                      to="/admin"
                      className="block bg-white dark:bg-gray-800 text-nba-blue px-4 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 transition font-semibold text-center mb-2"
                      onClick={closeMobileMenu}
                    >
                      {t('nav.admin')}
                    </Link>
                    <div className="text-sm px-4 py-2 text-center">{user.username}</div>
                    <button
                      onClick={() => {
                        logout();
                        closeMobileMenu();
                      }}
                      className="w-full bg-red-600 px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/apply"
                      className="block bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold text-center mb-2"
                      onClick={closeMobileMenu}
                    >
                      {t('nav.apply')}
                    </Link>
                    <Link
                      to="/login"
                      className="block bg-white dark:bg-gray-800 text-nba-blue px-4 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 transition font-semibold text-center"
                      onClick={closeMobileMenu}
                    >
                      {t('nav.login')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
